<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { uniqBy } from 'lodash'
import { $userList } from '@/api/users'
import { $getTeamLists } from '@/api/settings'
import type { CustomerSelect } from '@/models/customer'

interface UserSimple {
  id: number
  display_name: string
}

interface TeamMemberUser {
  id: number
  email: string
  user_role: number
  user_role_display: string
  first_name: string
  last_name: string
  customer: number | null
  customer_name?: string
  supplier: number | null
  suppliers: number[]
  supplier_name: string
  display_name?: string
}

type SelectUserItem = UserSimple | TeamMemberUser

interface TeamListSimple {
  id: number
  name: string
  users: number[]
  users_data: UserSimple[]
  created?: string
  modified?: string
}

interface TeamListTeamMembers {
  id: number
  name: string
  users: number[]
  users_data: TeamMemberUser[]
  created?: string
  modified?: string
}

type TeamList = TeamListSimple | TeamListTeamMembers

interface IProps {
  modelValue?: SelectUserItem | null
  label?: string
  customEventRole?: any
  teamMembers?: TeamMemberUser[] | null
  selectedUsers?: UserSimple[] | null
  customer?: CustomerSelect | null
  assigneeCampaignId?: number | null | undefined
  withTeamLists?: boolean
  teamListsPerPage?: number
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: null,
  label: 'Users',
  customEventRole: null,
  teamMembers: null,
  selectedUsers: null,
  customer: null,
  assigneeCampaignId: null,
  withTeamLists: false,
  teamListsPerPage: 10,
})

interface CustomEvents {
  (e: 'update:modelValue', val: SelectUserItem | null): void
  (e: 'add:team-members', val: TeamMemberUser[]): void
  (e: 'add:selected-users', val: UserSimple[]): void
}

const emit = defineEmits<CustomEvents>()

const userSelectAutocomplete = ref()

const activeTab = ref<'users' | 'team_lists'>('users')

// Detect current mode
const isTeamMembersMode = computed(() => !!props.teamMembers)
const isSelectedUsersMode = computed(() => !!props.selectedUsers)

// Current selected items from parent
const currentSelectedItems = computed<SelectUserItem[]>(() => {
  if (props.teamMembers) return props.teamMembers
  if (props.selectedUsers) return props.selectedUsers
  return []
})

const selectedItemIds = computed<number[]>(() => {
  return currentSelectedItems.value.map(item => item.id)
})

// Users tab state
const users = ref<SelectUserItem[]>([])
const usersCurrentPage = ref<number>(1)
const usersPerPage = 10
const usersSearchStr = ref<string>('')
const isNextUsersPageExist = ref<boolean>(false)
const usersLoading = ref<boolean>(false)

// Team Lists tab state
const teamLists = ref<TeamList[]>([])
const teamListsCurrentPage = ref<number>(1)
const teamListsSearchStr = ref<string>('')
const teamListsCount = ref<number>(0)
const teamListsLoading = ref<boolean>(false)
const selectedTeamList = ref<TeamList | null>(null)
const selectedTeamListUserIds = ref<number[]>([])

interface UsersFilters {
  get_all?: boolean
  is_simple?: boolean
  is_team_members?: boolean
  custom_event_role?: any
  search?: string
  per_page?: number
  page?: number
  customer?: number
  user_role?: number
  customer_or_null_customer_or_admin?: string
  assignee_list_campaign_id?: number | undefined | null
}

const getUserTitle = (user: SelectUserItem) => {
  if ('display_name' in user && user.display_name) return user.display_name

  if ('first_name' in user || 'last_name' in user) {
    return `${(user as TeamMemberUser).first_name ?? ''} ${(user as TeamMemberUser).last_name ?? ''}`.trim()
  }

  return ''
}

const isTeamMemberUser = (user: SelectUserItem): user is TeamMemberUser => {
  return 'email' in user
}

const resetUsersState = () => {
  users.value = []
  usersCurrentPage.value = 1
  usersSearchStr.value = ''
  isNextUsersPageExist.value = false
}

const resetTeamListsState = () => {
  teamLists.value = []
  teamListsCurrentPage.value = 1
  teamListsSearchStr.value = ''
  teamListsCount.value = 0
  selectedTeamList.value = null
  selectedTeamListUserIds.value = []
}

const selectedTeamListUsers = computed<SelectUserItem[]>(() => {
  return selectedTeamList.value?.users_data ?? []
})

const availableSelectedTeamListUsers = computed<SelectUserItem[]>(() => {
  return selectedTeamListUsers.value.filter(user => !selectedItemIds.value.includes(user.id))
})

const isAllSelectedInTeamList = computed<boolean>(() => {
  const availableIds = availableSelectedTeamListUsers.value.map(user => user.id)

  if (!availableIds.length) return false

  return availableIds.every(id => selectedTeamListUserIds.value.includes(id))
})

const isIndeterminateSelectedInTeamList = computed<boolean>(() => {
  const availableIds = availableSelectedTeamListUsers.value.map(user => user.id)
  const selectedCount = availableIds.filter(id => selectedTeamListUserIds.value.includes(id)).length

  return selectedCount > 0 && selectedCount < availableIds.length
})

const getUsers = async () => {
  usersLoading.value = true

  const filters: UsersFilters = {
    get_all: true,
    is_simple: isTeamMembersMode.value ? undefined : true,
    is_team_members: isTeamMembersMode.value ? true : undefined,
    assignee_list_campaign_id: props.assigneeCampaignId ? props.assigneeCampaignId : undefined,
    custom_event_role: props.customEventRole?.id ? props.customEventRole.id : undefined,
    search: usersSearchStr.value || undefined,
    per_page: usersPerPage,
    page: usersCurrentPage.value,
  }

  if (isTeamMembersMode.value) {
    if (props?.customer?.id && usersSearchStr.value) {
      filters.customer_or_null_customer_or_admin = JSON.stringify({ customer: props.customer.id })
    }

    if (props?.customer?.id && !usersSearchStr.value) {
      filters.customer = props.customer.id
    }
    else if (!props?.customer?.id) {
      filters.user_role = 0
    }
  }

  try {
    const res = await $userList(filters)

    users.value = uniqBy(
      [...users.value, ...res.data.results],
      'id'
    )

    isNextUsersPageExist.value = !!res.data.next
  }
  catch (e) {
    console.error(e)
  }
  finally {
    usersLoading.value = false
  }
}

const getTeamLists = async () => {
  teamListsLoading.value = true

  try {
    const requestParams: Record<string, any> = {
      page: teamListsCurrentPage.value,
      per_page: props.teamListsPerPage,
      name__icontains: teamListsSearchStr.value || undefined,
    }

    /**
     * Team Members mode:
     * full users_data
     *
     * Selected Users mode:
     * simple users_data by default
     * [{ id, display_name }]
     */
    if (isTeamMembersMode.value) {
      requestParams.is_team_members_user_data = true
    }

    const { data } = await $getTeamLists(requestParams)

    teamLists.value = teamListsCurrentPage.value === 1
      ? data.results
      : [...teamLists.value, ...data.results]

    teamListsCount.value = data.count
  }
  catch (e) {
    console.error(e)
  }
  finally {
    teamListsLoading.value = false
  }
}

const onFocusUserSelect = async (isFocused: boolean) => {
  if (!isFocused) return

  if (activeTab.value === 'users') {
    if (!users.value.length && !props.modelValue) {
      resetUsersState()
      await getUsers()
    }
    return
  }

  if (activeTab.value === 'team_lists' && !teamLists.value.length) {
    resetTeamListsState()
    await getTeamLists()
  }
}

const onLoadMoreUsers = async () => {
  usersCurrentPage.value += 1
  await getUsers()
}

const onLoadMoreTeamLists = async () => {
  teamListsCurrentPage.value += 1
  await getTeamLists()
}

const handleUserSearch = useDebounceFn((searchStr: string) => {
  if (activeTab.value !== 'users') return

  users.value = []
  usersCurrentPage.value = 1
  usersSearchStr.value = searchStr
  getUsers()
}, 500)

const handleTeamListsSearch = useDebounceFn((searchStr: string) => {
  teamLists.value = []
  teamListsCurrentPage.value = 1
  teamListsSearchStr.value = searchStr
  selectedTeamList.value = null
  selectedTeamListUserIds.value = []
  getTeamLists()
}, 500)

const onUpdateModelValue = (newValue: SelectUserItem | null) => {
  emit('update:modelValue', newValue)

  if (props.teamMembers || props.selectedUsers) {
    nextTick(() => {
      if (userSelectAutocomplete.value) {
        userSelectAutocomplete.value.menu = false
        userSelectAutocomplete.value.search = ''
      }
    })
  }
}

const onChangeTab = async (tab: 'users' | 'team_lists') => {
  activeTab.value = tab

  if (tab === 'users' && !users.value.length) {
    await getUsers()
  }

  if (tab === 'team_lists' && !teamLists.value.length) {
    await getTeamLists()
  }
}

const onSelectTeamList = (teamList: TeamList) => {
  selectedTeamList.value = teamList

  const availableIds = (teamList.users_data || [])
    .filter((user: any) => !selectedItemIds.value.includes(user.id))
    .map((user: any) => user.id)

  selectedTeamListUserIds.value = [...availableIds]
}

const toggleSelectAllTeamListUsers = () => {
  const availableIds = availableSelectedTeamListUsers.value.map(user => user.id)

  if (!availableIds.length) {
    selectedTeamListUserIds.value = []
    return
  }

  if (isAllSelectedInTeamList.value) {
    selectedTeamListUserIds.value = []
    return
  }

  selectedTeamListUserIds.value = [...availableIds]
}

const onToggleTeamListUser = (userId: number, checked: boolean) => {
  if (checked) {
    selectedTeamListUserIds.value = uniqBy(
      [
        ...selectedTeamListUserIds.value.map(id => ({ id })),
        { id: userId },
      ],
      'id'
    ).map(item => item.id)

    return
  }

  selectedTeamListUserIds.value = selectedTeamListUserIds.value.filter(id => id !== userId)
}

const addSelectedUsersFromTeamList = () => {
  if (!selectedTeamList.value) return

  const selectedUsersFromTeamList = selectedTeamList.value.users_data.filter(user =>
    selectedTeamListUserIds.value.includes(user.id)
  )

  if (!selectedUsersFromTeamList.length) return

  if (isTeamMembersMode.value) {
    emit('add:team-members', selectedUsersFromTeamList as TeamMemberUser[])
  }
  else {
    emit('add:selected-users', selectedUsersFromTeamList as UserSimple[])
  }

  nextTick(() => {
    if (userSelectAutocomplete.value) {
      userSelectAutocomplete.value.menu = false
    }
  })

  selectedTeamList.value = null
  selectedTeamListUserIds.value = []
}

watch(() => props.customEventRole, () => {
  emit('update:modelValue', null)
  resetUsersState()
  getUsers()
})

// On change Customer - load users with new customer prop
watch(() => props.customer, () => {
  if (!isTeamMembersMode.value) return

  emit('update:modelValue', null)
  resetUsersState()
  getUsers()
})

// On change AssigneeCampaignId - load users with new assigneeCampaignId prop
watch(() => props.assigneeCampaignId, () => {
  if (props.assigneeCampaignId) {
    emit('update:modelValue', null)
    resetUsersState()
    getUsers()
  }
})

watch(() => props.teamMembers, () => {
  if (!selectedTeamList.value) return

  const availableIds = (selectedTeamList.value?.users_data || [])
    .filter((user: any) => !selectedItemIds.value.includes(user.id))
    .map((user: any) => user.id)

  selectedTeamListUserIds.value = selectedTeamListUserIds.value.filter(id => availableIds.includes(id))
}, { deep: true })

watch(() => props.selectedUsers, () => {
  if (!selectedTeamList.value) return

  const availableIds = (selectedTeamList.value?.users_data || [])
    .filter((user: any) => !selectedItemIds.value.includes(user.id))
    .map((user: any) => user.id)

  selectedTeamListUserIds.value = selectedTeamListUserIds.value.filter(id => availableIds.includes(id))
}, { deep: true })
</script>

<template>
  <VAutocomplete
    ref="userSelectAutocomplete"
    :model-value="activeTab === 'users' ? modelValue : null"
    :items="activeTab === 'users' ? [...users] : []"
    :return-object="true"
    :item-title="getUserTitle"
    item-value="id"
    :label="label"
    density="compact"
    no-filter
    clearable
    :menu-props="{ maxHeight: 650 }"
    @update:modelValue="newValue => onUpdateModelValue(newValue)"
    @update:focused="onFocusUserSelect"
    @update:search="handleUserSearch"
  >
    <template #prepend-item>
      <div class="pa-3 pb-2">
        <VTabs
          :model-value="activeTab"
          color="primary"
          density="compact"
          @update:modelValue="value => onChangeTab(value)"
        >
          <VTab value="users">
            Users
          </VTab>

<!--          // TODO: Uncomment after Team Lists integration-->
<!--          <VTab-->
<!--            v-if="withTeamLists"-->
<!--            value="team_lists"-->
<!--          >-->
<!--            Team Lists-->
<!--          </VTab>-->
        </VTabs>
      </div>

      <VDivider />
    </template>

    <template
      v-if="activeTab === 'users' && (teamMembers || selectedUsers)"
      #item="{ item, props: itemProps }"
    >
      <VListItem
        v-bind="itemProps"
        :disabled="selectedItemIds.includes(item?.value)"
        :active="selectedItemIds.includes(item?.value) ? 'primary' : false"
      />
    </template>

    <template #no-data>
      <template v-if="activeTab === 'users'">
        <div
          v-if="usersLoading"
          class="d-flex justify-center align-center pa-4"
        >
          Loading...
          <VProgressCircular
            class="ml-2"
            size="22"
            indeterminate
            color="white"
          />
        </div>

        <div
          v-else
          class="d-flex justify-center align-center pa-4"
        >
          Users not found
        </div>
      </template>

<!--      // TODO: Uncomment after Team Lists integration-->
<!--      <template v-else-if="activeTab === 'team_lists'">-->
<!--        <div class="pa-3" style="min-width: 700px;">-->
<!--          <VTextField-->
<!--            :model-value="teamListsSearchStr"-->
<!--            label="Search Team Lists"-->
<!--            density="compact"-->
<!--            clearable-->
<!--            hide-details-->
<!--            @update:modelValue="value => handleTeamListsSearch(String(value ?? ''))"-->
<!--          />-->

<!--          <div class="mt-3 d-flex" style="gap: 12px; min-height: 360px;">-->
<!--            <div style="width: 42%; min-width: 260px;">-->
<!--              <div class="text-subtitle-1 mb-1">-->
<!--                Team Lists-->
<!--              </div>-->

<!--              <VList-->
<!--                border-->
<!--                rounded="lg"-->
<!--                class="py-0"-->
<!--                max-height="340"-->
<!--                style="overflow-y: auto;"-->
<!--              >-->
<!--                <VListItem-->
<!--                  v-for="teamList in teamLists"-->
<!--                  :key="teamList.id"-->
<!--                  :active="selectedTeamList?.id === teamList.id"-->
<!--                  @click="onSelectTeamList(teamList)"-->
<!--                >-->
<!--                  <template #title>-->
<!--                    {{ teamList.name }}-->
<!--                  </template>-->

<!--                  <template #subtitle>-->
<!--                    {{ teamList.users_data?.length || 0 }} users-->
<!--                  </template>-->
<!--                </VListItem>-->

<!--                <div-->
<!--                  v-if="teamListsLoading"-->
<!--                  class="pa-4 text-center"-->
<!--                >-->
<!--                  <VProgressCircular indeterminate size="22" />-->
<!--                </div>-->

<!--                <div-->
<!--                  v-else-if="!teamLists.length"-->
<!--                  class="pa-4 text-center"-->
<!--                >-->
<!--                  Team Lists not found-->
<!--                </div>-->
<!--              </VList>-->

<!--              <VBtn-->
<!--                v-if="teamLists.length < teamListsCount"-->
<!--                class="mt-1 py-1"-->
<!--                size=""-->
<!--                block-->
<!--                variant="tonal"-->
<!--                color="secondary"-->
<!--                :loading="teamListsLoading"-->
<!--                @click="onLoadMoreTeamLists"-->
<!--              >-->
<!--                Load more Team Lists-->
<!--              </VBtn>-->
<!--            </div>-->

<!--            <div style="width: 58%;">-->
<!--              <div class="d-flex align-center justify-space-between mb-1">-->
<!--                <div class="text-subtitle-2">-->
<!--                  {{-->
<!--                    selectedTeamList-->
<!--                      ? `Users from "${selectedTeamList?.name}"`-->
<!--                      : 'Select Team List'-->
<!--                  }}-->
<!--                </div>-->

<!--                <div-->
<!--                  v-if="selectedTeamList"-->
<!--                  class="d-flex align-center"-->
<!--                  style="gap: 8px;"-->
<!--                >-->
<!--                  <VCheckbox-->
<!--                    :model-value="isAllSelectedInTeamList"-->
<!--                    :indeterminate="isIndeterminateSelectedInTeamList"-->
<!--                    hide-details-->
<!--                    density="compact"-->
<!--                    label="Select all"-->
<!--                    @update:modelValue="toggleSelectAllTeamListUsers"-->
<!--                  />-->

<!--                  <VBtn-->
<!--                    size=""-->
<!--                    color="primary"-->
<!--                    class="pa-1"-->
<!--                    :disabled="!selectedTeamListUserIds.length"-->
<!--                    @click="addSelectedUsersFromTeamList"-->
<!--                  >-->
<!--                    Add selected-->
<!--                    <span v-if="selectedTeamListUserIds.length">-->
<!--                      {{ ` (${selectedTeamListUserIds.length})` }}-->
<!--                    </span>-->
<!--                  </VBtn>-->
<!--                </div>-->
<!--              </div>-->

<!--              <VList-->
<!--                border-->
<!--                rounded="lg"-->
<!--                class="py-0"-->
<!--                max-height="340"-->
<!--                style="overflow-y: auto;"-->
<!--              >-->
<!--                <template v-if="selectedTeamList">-->
<!--                  <VListItem-->
<!--                    v-for="user in selectedTeamListUsers"-->
<!--                    :key="user.id"-->
<!--                    :disabled="selectedItemIds.includes(user.id)"-->
<!--                  >-->
<!--                    <template #prepend>-->
<!--                      <VCheckbox-->
<!--                        :model-value="selectedTeamListUserIds.includes(user.id)"-->
<!--                        :disabled="selectedItemIds.includes(user.id)"-->
<!--                        hide-details-->
<!--                        density="compact"-->
<!--                        @update:modelValue="checked => onToggleTeamListUser(user.id, !!checked)"-->
<!--                      />-->
<!--                    </template>-->

<!--                    <template #title>-->
<!--                      {{ getUserTitle(user) }}-->
<!--                    </template>-->

<!--                    <template v-if="isTeamMemberUser(user)" #subtitle>-->
<!--                      <div>{{ user.email }}</div>-->
<!--                      <div class="text-caption">-->
<!--                        {{ user.user_role_display }}-->
<!--                        <span v-if="user.customer_name"> • {{ user.customer_name }}</span>-->
<!--                        <span v-else-if="user.supplier_name"> • {{ user.supplier_name }}</span>-->
<!--                      </div>-->
<!--                    </template>-->

<!--                    <template-->
<!--                      v-if="selectedItemIds.includes(user.id)"-->
<!--                      #append-->
<!--                    >-->
<!--                      <VChip-->
<!--                        size="small"-->
<!--                        color="primary"-->
<!--                        variant="tonal"-->
<!--                      >-->
<!--                        Already added-->
<!--                      </VChip>-->
<!--                    </template>-->
<!--                  </VListItem>-->

<!--                  <div-->
<!--                    v-if="!selectedTeamListUsers.length"-->
<!--                    class="pa-4 text-center"-->
<!--                  >-->
<!--                    No users in this Team List-->
<!--                  </div>-->
<!--                </template>-->

<!--                <div-->
<!--                  v-else-->
<!--                  class="pa-4 text-center"-->
<!--                >-->
<!--                  Select Team List to view users-->
<!--                </div>-->
<!--              </VList>-->
<!--            </div>-->
<!--          </div>-->
<!--        </div>-->
<!--      </template>-->
    </template>

    <template
      v-if="activeTab === 'users' && isNextUsersPageExist"
      #append-item
    >
      <VBtn
        color="default"
        block
        :disabled="usersLoading"
        @click="onLoadMoreUsers"
      >
        Load more...
        <VProgressCircular
          v-if="usersLoading"
          class="ml-2"
          size="22"
          indeterminate
          color="white"
        />
      </VBtn>
    </template>
  </VAutocomplete>
</template>
